/**
 * Sidebar: renders NAV (nav-config) with two states — expanded (240px) and
 * collapsed (64px, icon-only + click-opened flyout). Groups are accordions when expanded.
 * Footer: language toggle + current user + sign out. No RBAC gating yet (phase A).
 */
import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useMatchRoute } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { authClient } from '../lib/auth-client'
import { useT } from '../i18n/language-provider'
import type { AccessProfile } from '@system/shared'
import { FOOTER_LINKS, NAV, hasNavAccess, isLeafActive, type NavItem, type NavLeaf } from './nav-config'
import { Icon } from './ui/icon'
import { BrandLogo } from './brand-logo'
import { useAccess } from '../lib/access-context'
import { getQuizMyAccess } from '../lib/api-client-quiz'

/** Short role label for the sidebar user button — most specific role first. */
function roleLabel(a: AccessProfile): string {
  if (a.isAdmin) return 'Admin'
  if (a.isHr) return 'HR'
  if (a.isCmo) return 'CMO'
  if (a.isCto) return 'CTO'
  if (a.isAso) return 'ASO'
  if (a.isUa) return 'Marketing'
  if (a.isMonet || a.monetOnly) return 'Monetization'
  if (a.isDev || a.isDevRestricted) return 'Developer'
  if (a.isEmployee) return 'Nhân viên'
  return 'Khách'
}

function leafClasses(active: boolean) {
  return [
    'block rounded-md px-3 py-2 text-sm transition-colors',
    active
      ? 'bg-primary-soft text-primary font-medium'
      : 'text-fg-soft hover:bg-hover hover:text-fg',
  ].join(' ')
}

/** Badge shown to SPN004 (canToggleNav) next to an entry they switched off — everyone else never
 *  sees the entry at all, so there is nothing for the badge to sit on. */
function OffBadge({ label }: { label: string }) {
  return (
    <span className="ml-1.5 rounded bg-amber-100 px-1 py-0.5 text-[10px] font-medium leading-none text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
      {label}
    </span>
  )
}

export function Sidebar({
  collapsed,
  onToggleCollapse,
  onNavigate,
}: {
  collapsed: boolean
  onToggleCollapse: () => void
  onNavigate?: () => void
}) {
  const t = useT()
  const { data: session } = authClient.useSession()
  const access = useAccess()
  const navigate = useNavigate()

  // Restricted Developer-only viewers see just the Developer report leaf.
  // Monetization-only operators see just the Monetization group.
  // Otherwise: filter each leaf/link by its `access` key, drop groups left with no children.
  const filterNav = (items: NavItem[]): NavItem[] =>
    items
      .map((i) => (i.kind === 'group' ? { ...i, children: i.children.filter((c) => hasNavAccess(c.access, access)) } : i))
      .filter((i) => (i.kind === 'group' ? i.children.length > 0 : hasNavAccess(i.access, access)))

  // Quiz group visibility (user 260721): hosts see all leaves on every device; everyone
  // else keeps only the play leaf, and the whole group renders on MOBILE only.
  const { data: quizAccess } = useQuery({
    queryKey: ['quiz-my-access'],
    queryFn: async () => { const r = await getQuizMyAccess(); return r.success ? r.data : { isHost: false, isAdmin: false } },
    staleTime: 5 * 60_000,
    enabled: !!session,
  })
  const isQuizHost = quizAccess?.isHost === true
  const shapeQuizGroup = (items: NavItem[]): NavItem[] =>
    items.map((i) => {
      if (i.kind !== 'group' || i.id !== 'quiz') return i
      return { ...i, children: isQuizHost ? i.children : i.children.filter((c) => c.to === '/quiz') }
    })
  /** Class hiding the quiz group on ≥sm screens for non-hosts (players are mobile-only). */
  const quizGroupCls = (item: NavItem): string =>
    item.kind === 'group' && item.id === 'quiz' && !isQuizHost ? 'sm:hidden' : ''
  // Sidebar toggle (user decision 2026-09-28): SPN004 (canToggleNav) sees every entry, badged
  // where it is switched off; everyone else — including every branch above (restricted, monetOnly,
  // the normal filter) — loses the entry outright, same as if `access` lacked the role for it.
  const isOff = (to: string) => access.canToggleNav && access.disabledNav.includes(to)
  const gateDisabled = (items: NavItem[]): NavItem[] =>
    items
      .map((i) => (i.kind === 'group' ? { ...i, children: i.children.filter((c) => access.canToggleNav || !access.disabledNav.includes(c.to)) } : i))
      .filter((i) => (i.kind === 'group' ? i.children.length > 0 : access.canToggleNav || !access.disabledNav.includes(i.to)))

  // Per-page rules (user decision 29/09/2026): a granted page joins whatever the branch below
  // shows, a blocked one leaves it — admins included. Rebuilt from NAV so order stays the NAV order.
  const applyPageRules = (items: NavItem[]): NavItem[] => {
    if (access.pageGrants.length === 0 && access.pageBlocks.length === 0) return items
    const shown = new Set<string>()
    for (const i of items) {
      if (i.kind === 'link') shown.add(i.to)
      else for (const c of i.children) shown.add(c.to)
    }
    for (const to of access.pageGrants) shown.add(to)
    for (const to of access.pageBlocks) shown.delete(to)
    return NAV
      .map((i) => (i.kind === 'group' ? { ...i, children: i.children.filter((c) => shown.has(c.to)) } : i))
      .filter((i) => (i.kind === 'group' ? i.children.length > 0 : shown.has(i.to)))
  }

  const nav: NavItem[] = gateDisabled(applyPageRules(
    access.isDevRestricted
      ? [
          // Internal dev employees keep the Dashboard and game links; external dev-group viewers stay dev-only.
          ...(access.isEmployee ? NAV.filter((i) => i.kind === 'link' && (i.to === '/' || (i.to === '/game' && hasNavAccess(i.access, access)))) : []),
          // Reports always; Encrypt too for internal DEV-department members (route + API already
          // allow it — hiding the link here was why restricted devs "couldn't reach" the tool).
          ...NAV.flatMap((i) => (i.kind === 'group' && i.id === 'developer'
            ? [{ ...i, children: i.children.filter((c) =>
                c.to === '/developer' || (c.to === '/developer/encrypt' && access.isDev && access.isEmployee)) }]
            : [])),
        ]
      : access.monetOnly
        ? NAV.filter((i) => i.kind === 'group' && i.id === 'monetization')
        : shapeQuizGroup(filterNav(NAV)),
  ))
  // Internal employees keep the personal links even when dev/monet-restricted;
  // only external (non-employee) viewers get the bare menu.
  //
  // '/help' is excluded here — it renders separately, below sign-out, for everyone signed in
  // regardless of this branch (own block further down). It is still in FOOTER_LINKS itself, so
  // toggleableNavGroups/disabledNavFor pick it up; this list would otherwise render it twice.
  const footerLinks = (access.isDevRestricted || access.monetOnly) && !access.isEmployee
    ? []
    : FOOTER_LINKS
        .filter((l) => l.to !== '/help')
        .filter((l) =>
          // Employees only. The route and the API both refuse anyone else, so this just avoids
          // offering a link that leads to a 403.
          (l.to === '/handbook' || l.to === '/travel-expense' ? access.isEmployee || access.pageGrants.includes(l.to) : true) &&
          !access.pageBlocks.includes(l.to) &&
          (access.canToggleNav || !access.disabledNav.includes(l.to)),
        )
        .map((l) => ({ to: l.to, label: t(l.labelKey), off: isOff(l.to) }))
  const matchRoute = useMatchRoute()
  // One group open at a time. A record of booleans let every group be open at once, which on a
  // sidebar this tall pushed the lower groups off screen. A single id makes that state
  // unrepresentable rather than merely discouraged.
  const [openGroup, setOpenGroup] = useState<string | null>(null)
  const [footerOpen, setFooterOpen] = useState(false)

  const userName = session?.user?.name ?? ''
  const userAvatar = session?.user?.image ?? ''
  const userInitial = userName ? userName.charAt(0).toUpperCase() : '?'

  const isActive = (to: string, exact = false) => Boolean(matchRoute({ to, fuzzy: !exact }))
  const toggleGroup = (id: string) => setOpenGroup((current) => (current === id ? null : id))

  async function handleSignOut() {
    try {
      await authClient.signOut()
      void navigate({ to: '/login' })
    } catch (err: unknown) {
      console.error('Sign out failed:', err)
    }
  }

  // Collapsed: a flyout closes once a page is picked. Expanded: the accordion stays open, as before.
  const onLeafClick = () => {
    if (collapsed) setOpenGroup(null)
    onNavigate?.()
  }

  // Collapsed flyout closes on a click outside the sidebar nav, or on Escape.
  const navRef = useRef<HTMLElement>(null)
  useEffect(() => {
    if (!collapsed || openGroup === null) return
    const onDown = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) setOpenGroup(null)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpenGroup(null) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [collapsed, openGroup])

  const Leaf = ({ leaf }: { leaf: NavLeaf }) => (
    <Link to={leaf.to} onClick={onLeafClick} className={leafClasses(isLeafActive(leaf, isActive))}>
      {t(leaf.labelKey)}
      {isOff(leaf.to) && <OffBadge label={t('navToggles.offBadge')} />}
    </Link>
  )

  return (
    <div id="sidebar" className="flex h-full flex-col bg-surface">
      {/* Header: logo + collapse toggle. Collapsed (64px) can't fit the mark and the
          toggle side by side, so they stack instead. */}
      <div
        className={`flex border-b border-border-soft px-3 py-4 ${
          collapsed ? 'flex-col items-center gap-2' : 'items-center justify-between'
        }`}
      >
        <BrandLogo variant={collapsed ? 'mark' : 'wordmark'} className={collapsed ? 'h-6 w-6' : 'h-6 w-auto'} />
        <button
          onClick={onToggleCollapse}
          className="inline-flex items-center justify-center rounded p-1 text-muted hover:bg-hover"
          title={t('layout.toggleSidebar')}
          aria-label={t('layout.toggleSidebar')}
        >
          <Icon name="menu" className="h-5 w-5" />
        </button>
      </div>

      {/* Nav. Collapsed: overflow must stay visible — a scroll container would clip the
          absolute-positioned flyouts (they render outside the 64px rail). */}
      <nav ref={navRef} id="sidebar-nav" className={`flex-1 space-y-0.5 px-2 py-3 ${collapsed ? 'overflow-visible' : 'overflow-y-auto'}`}>
        {nav.map((item) => {
          if (item.kind === 'link') {
            const active = isActive(item.to, item.to === '/')
            return (
              <Link
                key={item.to}
                to={item.to}
                onClick={onNavigate}
                title={collapsed ? t(item.labelKey) : undefined}
                className={[leafClasses(active), 'flex items-center gap-2', collapsed && 'justify-center'].filter(Boolean).join(' ')}
              >
                <Icon name={item.icon} className="h-5 w-5 shrink-0" />
                {!collapsed && (
                  <span className="flex items-center">
                    {t(item.labelKey)}
                    {isOff(item.to) && <OffBadge label={t('navToggles.offBadge')} />}
                  </span>
                )}
              </Link>
            )
          }

          // group
          const open = openGroup === item.id
          return (
            <div key={item.id} className={[collapsed ? 'relative' : '', quizGroupCls(item)].filter(Boolean).join(' ') || undefined}>
              <button
                id={`sidebar-group-${item.id}`}
                // Both states open on click only (user request 2026-09-29): expanded toggles the
                // accordion, collapsed toggles the flyout. Hovering opens nothing.
                onClick={() => toggleGroup(item.id)}
                aria-expanded={open}
                title={collapsed ? t(item.labelKey) : undefined}
                className={[
                  'flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-fg-soft transition-colors hover:bg-hover hover:text-fg',
                  collapsed && 'justify-center',
                ].filter(Boolean).join(' ')}
              >
                <Icon name={item.icon} className="h-5 w-5 shrink-0" />
                {!collapsed && <span className="flex-1 text-left">{t(item.labelKey)}</span>}
                {!collapsed && (open ? <Icon name="chevron-down" className="h-3.5 w-3.5 text-faint" /> : <Icon name="chevron-right" className="h-3.5 w-3.5 text-faint" />)}
              </button>

              {/* expanded: accordion children */}
              {!collapsed && open && (
                <div className="ml-4 space-y-0.5 border-l border-border-soft pl-2">
                  {item.children.map((c) => <Leaf key={c.to} leaf={c} />)}
                </div>
              )}

              {/* collapsed: click-opened flyout, closed by picking a page, clicking outside the
                  sidebar nav, or Escape. */}
              {collapsed && open && (
                <div id={`sidebar-flyout-${item.id}`} className="absolute left-full top-0 z-30 pl-1">
                  <div className="min-w-44 rounded-md border border-border bg-surface p-1 shadow-lg">
                    <div className="px-2 py-1 text-xs font-semibold text-faint">{t(item.labelKey)}</div>
                    {item.children.map((c) => <Leaf key={c.to} leaf={c} />)}
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </nav>

      {/* Footer: click user block to open the menu (erp-style) */}
      <div id="sidebar-footer" className="relative border-t border-border px-3 py-3">
        {/* User button (toggles menu) */}
        <button
          id="sidebar-user-btn"
          onClick={() => setFooterOpen((o) => !o)}
          className={['flex w-full items-center gap-2 rounded-md p-1 hover:bg-hover', collapsed && 'justify-center'].filter(Boolean).join(' ')}
        >
          {userAvatar ? (
            <img src={userAvatar} alt={userName} referrerPolicy="no-referrer" className="h-9 w-9 shrink-0 rounded-full object-cover" />
          ) : (
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-soft text-sm font-semibold text-primary">{userInitial}</div>
          )}
          {!collapsed && (
            <>
              <div className="min-w-0 flex-1 text-left">
                <div className="truncate text-sm font-semibold text-fg" title={userName}>{userName}</div>
                <div className="text-xs text-muted">{roleLabel(access)}</div>
              </div>
              {footerOpen ? <Icon name="chevron-up" className="h-3.5 w-3.5 text-faint" /> : <Icon name="chevron-down" className="h-3.5 w-3.5 text-faint" />}
            </>
          )}
        </button>

        {/* Menu: lang + theme + sign out */}
        {footerOpen && (
          <div
            className={[
              'space-y-2 rounded-md',
              collapsed
                ? 'absolute bottom-2 left-full z-20 ml-1 w-44 border border-border bg-surface p-2 shadow-lg'
                : 'mt-2 px-1',
            ].join(' ')}
          >
            {footerLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => { setFooterOpen(false); onNavigate?.() }}
                className="flex items-center rounded px-1 py-1 text-sm text-fg-soft hover:bg-hover hover:text-fg"
              >
                {l.label}
                {l.off && <OffBadge label={t('navToggles.offBadge')} />}
              </Link>
            ))}
            <div className="my-1 border-t border-border-soft" />
            <button
              id="sidebar-signout-btn"
              onClick={() => { void handleSignOut() }}
              className="block w-full rounded px-1 py-1 text-left text-sm text-red-500 hover:bg-hover hover:text-red-700"
            >
              {t('common.signOut')}
            </button>
            {/* Help sits under sign-out, for everyone: the guides to the tools on this system.
                Same disabled-hide / SPN004-badge treatment as every other FOOTER_LINKS entry —
                its visibility rule (open to everyone signed in) is otherwise unchanged. */}
            {(access.canToggleNav || !access.disabledNav.includes('/help')) && !access.pageBlocks.includes('/help') && (
              <>
                <div className="my-1 border-t border-border-soft" />
                <Link
                  id="sidebar-help-link"
                  to="/help"
                  onClick={() => { setFooterOpen(false); onNavigate?.() }}
                  className="flex items-center rounded px-1 py-1 text-sm text-fg-soft hover:bg-hover hover:text-fg"
                >
                  {t('nav.help')}
                  {isOff('/help') && <OffBadge label={t('navToggles.offBadge')} />}
                </Link>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
